import _ from 'lodash';
import React, { useContext, useState, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as Constants from 'src/components/common/Constants';
import Dialog from 'src/components/common/Dialog.redesign';
import Button from 'src/components/controls/forms/redesign/FormButton';
import ContractOwnershipList from 'src/models/ContractOwnershipList';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import AgreementUtil from 'src/util/AgreementUtil';
import useFetch from 'use-http';
import AttachLeaseholdCard from './ManageLeaseholdDialogComponents/AttachLeaseholdCard';
import LeaseholdSearchResults from './ManageLeaseholdDialogComponents/LeaseholdSearchResults';

export interface IManageLeaseholdDialog2Props {
    navigate: (path: string) => void;
    onCloseDialog?: () => void;
}

export type IManageLeaseholdDialog2StateProps = {
    ownershipId: number;
    horizonNumber?: number;
};

type IManageLeaseholdDialog2PageProps = IManageLeaseholdDialog2Props & IManageLeaseholdDialog2StateProps;

const ManageLeaseholdDialog2 = (props: IManageLeaseholdDialog2PageProps) => {
    // tslint:disable: max-line-length

    const appContext = useContext<IAppContextState>(AppContext);
    const { get, response } = useFetch();
    const { getValues, register } = useForm();
    const [isLeaseholdSearchLoading, setIsLeaseholdSearchLoading] = useState(false);
    const [contracts, setContracts] = useState<Array<ContractOwnershipList>>(null);
    const [searchResults, setSearchResults] = useState<Array<ContractOwnershipList>>([]);
    const [isAgreementFound, setIsAgreementFound] = useState(undefined);
    const [typeSelected, setTypeSelected] = useState(false);

    const onNavigate = (contractId: number, contractholdId: number, contractType: string) => {
        let navPath = AgreementUtil.navToLeasehold(props.ownershipId, contractId, contractholdId, contractType);
        if (navPath.length > 0) {
            props.navigate(navPath);
            props.onCloseDialog();
        } else {
            window.alert('Invalid Agreement Type!');
            console.log('Invalid Agreement type for navigation: ', contractType);
        }
    };

    useEffect(() => {
        if (props.ownershipId > 0) {
            setSearchResults([]);
            setIsAgreementFound(undefined);
            getContracts();
        }
    }, [props.ownershipId]);

    const getContracts = async () => {
        const result: Array<ContractOwnershipList> = await get(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/leases/ownershipId/${props.ownershipId}/contracts/true`
        );

        if (response.ok) {
            setContracts(result);
        }
    };

    // Recording-info search (book/page or instrument #) takes precedence over the contract-number
    // search and runs across all contract types. Contract-number search remains type-scoped.
    const onSearchLeases = useCallback(
        _.debounce(() => {
            let rep = getValues();
            const hasRecordingInfo =
                rep.instrumentNumber?.length > 0 || (rep.book?.length > 0 && rep.page?.length > 0);

            if (hasRecordingInfo) {
                searchByRecordingInfo(rep.book, rep.page, rep.instrumentNumber);
            } else if (rep.searchType?.length > 0 && rep.searchValue?.length > 0) {
                searchContracts(rep.searchType, rep.searchValue);
            } else {
                setSearchResults([]);
                setIsAgreementFound(undefined);
            }
        }, 800),
        []
    );

    const checkType = () => {
        let rep = getValues();
        setTypeSelected(rep.searchType?.length > 0);
        onSearchLeases();
    };

    const searchContracts = async (searchType: string, searchValue: string) => {
        setIsLeaseholdSearchLoading(true);
        let contractPath: string;
        if (searchType === 'Oil and Gas Lease') {
            contractPath = `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/leases/lease-number/${searchValue}`;
        } else {
            contractPath = `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/leases/${searchType}/contract-number/${searchValue}`;
        }

        const results: ContractOwnershipList = await get(contractPath);

        if (response.ok && results?.agreementId !== undefined && (results.isInHand || !results.isGeneratedAgreement)) {
            setSearchResults([results]);
            setIsAgreementFound(true);
        } else {
            setSearchResults([]);
            setIsAgreementFound(false);
        }
        setIsLeaseholdSearchLoading(false);
    };

    const searchByRecordingInfo = async (book: string, page: string, instrumentNumber: string) => {
        setIsLeaseholdSearchLoading(true);

        const params = new URLSearchParams();
        if (book?.length > 0) {
            params.append('book', book);
        }
        if (page?.length > 0) {
            params.append('page', page);
        }
        if (instrumentNumber?.length > 0) {
            params.append('instrumentNumber', instrumentNumber);
        }

        const contractPath = `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/leases/recording-info?${params.toString()}`;

        const results: Array<ContractOwnershipList> = await get(contractPath);

        if (response.ok && Array.isArray(results)) {
            const validResults = results.filter((c) => c.isInHand || !c.isGeneratedAgreement);
            setSearchResults(validResults);
            setIsAgreementFound(validResults.length > 0);
        } else {
            setSearchResults([]);
            setIsAgreementFound(false);
        }
        setIsLeaseholdSearchLoading(false);
    };

    const contractCard = (contract: ContractOwnershipList, index: number, nav: boolean, newContract: boolean) => {
        return (
            <AttachLeaseholdCard
                agreement={contract}
                index={index}
                nav={nav}
                newAgreement={newContract}
                onNavigate={onNavigate}
            />
        );
    };

    const contractCardContainer = () => {
        if (contracts?.length > 0) {
            return contracts
                .filter((l) => l.isInHand === true || !l.isGeneratedAgreement)
                .sort((a, b) => { // sort by Agreement / Ownership Type first
                    if (a.agreementType > b.agreementType) {
                        return -1;
                    }
                    if (a.agreementType < b.agreementType) {
                        return 1;
                    }
                    return 0;
                }) // send Active Leases to the top by sorting it second
                .sort((a, b) => {
                    if (a.isActiveLease > b.isActiveLease) {
                        return -1;
                    }
                    if (a.isActiveLease < b.isActiveLease) {
                        return 1;
                    }
                    return 0;
                })
                .map((contract: ContractOwnershipList, index) => {
                    return contractCard(contract, index, true, false);
                });
        } else {
            return <div style={{ textAlign: 'center' }}>--No InHanded Contract Ownerships have been attached to this Ownership--</div>;
        }
    };

    const contractList = () => {
        return (
            <div style={{ marginBottom: '1rem' }}>
                <div>
                    <fieldset style={{ padding: '.3rem 0', borderWidth: '1px 0 0 0' }}>
                        <legend className="legend">Attached Contract Ownerships</legend>
                    </fieldset>
                </div>

                <div style={{ display: 'flex', padding: '.5rem' }}>
                    <div style={{ width: '12%' }}>Agreement Type</div>
                    <div style={{ width: '10%' }}>Agreement Number</div>
                    <div style={{ width: '10%' }}>Vol/Page | Inst#</div>
                    <div style={{ width: '10%' }}>Agreement Date</div>
                    <div style={{ width: '21%' }}>Lessor</div>
                    <div style={{ width: '21%' }}>Lessee</div>
                    <div style={{ width: '14%' }}>Status</div>
                </div>

                <div>{contractCardContainer()}</div>
            </div>
        );
    };

    const leaseSearch = () => {
        return (
            <div style={{ marginBottom: '1rem' }}>
                <fieldset style={{ padding: '.3rem 0', borderWidth: '1px 0 0 0' }}>
                    <legend className="legend">Search Existing Contract Ownerships</legend>
                </fieldset>

                <div>
                    <label style={{ marginRight: '1rem' }}>Contract Ownership Type:</label>

                    <select
                        style={{ height: '3rem', border: '0', borderBottom: '1px solid grey', marginRight: '1rem' }}
                        name="searchType"
                        id="searchType"
                        ref={register}
                        onChange={() => checkType()}
                    >
                        <option key={0} disabled={true} selected={true} hidden={true} value={''}>
                            -- Select --
                        </option>
                        {Object.values(Constants.contractTypeRef).map((leaseType, index) => {
                            return (
                                <option key={index} value={leaseType.label}>
                                    {leaseType.label}
                                </option>
                            );
                        })}
                    </select>

                    <label style={{ marginRight: '1rem' }}>Contract Ownership Number:</label>

                    <input
                        style={{ height: '3rem', border: '0', borderBottom: '1px solid grey', marginRight: '1rem' }}
                        type="text"
                        name="searchValue"
                        id="searchValue"
                        ref={register}
                        onChange={() => onSearchLeases()}
                    />
                </div>

                <div style={{ marginTop: '.5rem' }}>
                    <label style={{ marginRight: '1rem' }}>Book / Vol:</label>

                    <input
                        style={{ height: '3rem', border: '0', borderBottom: '1px solid grey', marginRight: '1rem', width: '8rem' }}
                        type="text"
                        name="book"
                        id="book"
                        ref={register}
                        onChange={() => onSearchLeases()}
                    />

                    <label style={{ marginRight: '1rem' }}>Page:</label>

                    <input
                        style={{ height: '3rem', border: '0', borderBottom: '1px solid grey', marginRight: '1rem', width: '8rem' }}
                        type="text"
                        name="page"
                        id="page"
                        ref={register}
                        onChange={() => onSearchLeases()}
                    />

                    <label style={{ marginRight: '1rem' }}>Instrument #:</label>

                    <input
                        style={{ height: '3rem', border: '0', borderBottom: '1px solid grey', marginRight: '1rem', width: '12rem' }}
                        type="text"
                        name="instrumentNumber"
                        id="instrumentNumber"
                        ref={register}
                        onChange={() => onSearchLeases()}
                    />
                </div>

                <LeaseholdSearchResults
                    searchResults={searchResults}
                    isLeaseholdSearchLoading={isLeaseholdSearchLoading}
                    isAgreementFound={isAgreementFound}
                    onNavigate={onNavigate}
                />
            </div>
        );
    };

    const createNewLease = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();
        let rep = getValues();

        onNavigate(0, 0, rep.searchType);
    };

    return (
        <Dialog title="Manage Contract Ownerships">
            <div style={{ width: '60vw' }}>
                <div>{contractList()}</div>

                <div>{leaseSearch()}</div>

                <div>
                    <Button text="New Contract" intent="primary" onClick={(e) => createNewLease(e)} disabled={!typeSelected} />
                    {!typeSelected && <span> Select Agreement Type to continue.</span>}
                </div>
            </div>
        </Dialog>
    );
};

export default ManageLeaseholdDialog2;



// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/ManageLeaseholdDialog.redesign.tsx